import cl from 'clsx'
import { mapValues } from 'lodash-es'

import { CreativeCell } from '@/features/analytics/invite-links/ui/InviteLinksTable/shared/cells/CreativeCell'
import { useInviteLinksStore } from '@/features/analytics/model'
import { AnalyticsTableRowType } from '@/features/analytics/types'

import { PlusIcon } from '@/shared/icons'
import EridIcon from '@/features/analytics/invite-links/icons/stones/Erid.svg?react'
import {
    useCreateVkOrdCreative,
    type UseCreateVkOrdCreative,
    type UseSavedErid,
    useUpdateMarkingFormData,
    useUpdateVkOrdCreative,
} from '@/shared/api'
import { ClickTooltip } from '@/shared/ui'
import { CamelIdObj } from '@synapex/tools/typescript'
import {
    ComponentPropsWithRef,
    forwardRef,
    ReactNode,
    useCallback,
    useMemo,
    useState,
} from 'react'
import { MarkingPartLayout } from '../../components/shared/MarkingPartLayout'
import { MARKING_DIALOG_LAYOUT } from '../../components/shared/MarkingPartLayout/data'
import classes from './Creative.module.scss'
import {
    KktuSelect,
    useKktuSelectState,
    type UseKktuSelectState,
} from './KktuSelect'
import {
    WorkspaceWithLinkDescriptor,
    workspaceWithLinkMethods,
} from '@synapex/shared2/entities'

const LAYOUT = {
    gap: 8,
    width: {
        kktu: 146,
        token: 118,
    },
}

const STYLE = {
    ...mapValues(LAYOUT.width, (width) => ({ width })),
    content: { gap: LAYOUT.gap },
    previewOverlay: {
        right: 0,
        left: 'auto',
        width: MARKING_DIALOG_LAYOUT.totalWidth,
    },
    previewContent: {
        left: 'auto',
        width: 334,
        right: (MARKING_DIALOG_LAYOUT.totalWidth - 334) / 2,
        '--tw-translate-x': 0,
    },
    creativeSelectTriggerNormal: {
        borderColor: '#056FFF',
    },
    creativeSelectTriggerError: {
        borderColor: '#FF5957',
    },
    labeledCheckbox: {
        top: 22,
    },
    cursorUnset: {
        cursor: 'unset',
    },
    clickTooltipContent: {
        width: 180,
    },
}

type FieldKey = 'kktu' | 'creative'
type FieldErrors = Record<FieldKey, string>

const useKktuAndErid = ({
    savedErid,
    savedKktu,
    onTouched,
    latestKktu,
}: UseSavedErid & {
    latestKktu: string | null
    onTouched: () => void
}): {
    erid: string | null
    kktuHook: UseKktuSelectState
} => {
    const kktuHook = useKktuSelectState({ initialValue: latestKktu, onTouched })

    if (savedErid) {
        return {
            erid: savedErid,
            kktuHook: {
                ...kktuHook,
                state: {
                    ...kktuHook.state,
                    value: savedKktu,
                },
            },
        }
    }

    return {
        erid: null,
        kktuHook,
    }
}

type UseValuesWithErrorsProps = UseSavedErid & {
    latestKktu: string | null
    isContractConfirmed: boolean
    contractId?: number | null
} & CamelIdObj<'creative', number | null | undefined>

const NO_ERRORS = {
    kktu: '',
    creative: '',
}

const useValuesWithErrors = ({
    contractId,
    creativeId,
    isContractConfirmed,
    ...savedEridKktu
}: UseValuesWithErrorsProps) => {
    const { contractError, eridError, setContractError, setEridError } =
        useInviteLinksStore()

    const [errors, _setErrors] = useState<FieldErrors>(NO_ERRORS)

    const kktuError = errors.kktu
    const clearErrorKktu = useCallback(() => {
        if (kktuError) _setErrors((s) => ({ ...s, kktu: '' }))
    }, [kktuError])

    const creativeError = errors.creative
    const clearErrorCreative = useCallback(() => {
        if (creativeError) _setErrors((s) => ({ ...s, creative: '' }))
    }, [creativeError])

    const hasErrors = useMemo(
        () =>
            Boolean(contractError) ||
            Boolean(kktuError) ||
            Boolean(creativeError) ||
            Boolean(eridError),
        [contractError, kktuError, creativeError, eridError]
    )

    const clearAllErrors = useCallback(() => {
        _setErrors(NO_ERRORS)
        setEridError(false)
    }, [setEridError])

    //
    const { erid, kktuHook } = useKktuAndErid({
        ...savedEridKktu,
        onTouched: clearErrorKktu,
    })

    //

    const kktuValue = kktuHook.state.value
    const setErrors = useCallback(() => {
        _setErrors({
            kktu: kktuValue ? '' : 'Укажите ККТУ',
            creative: creativeId ? '' : 'Выберите креатив',
        })
        setEridError(erid ? '' : 'Создайте токен')
        setContractError(
            contractId && isContractConfirmed
                ? ''
                : 'Для создания токена требуется договор'
        )
    }, [
        setContractError,
        contractId,
        creativeId,
        kktuValue,
        erid,
        setEridError,
        isContractConfirmed,
    ])

    return {
        erid,
        kktuHook,
        errors,
        eridError,
        setErrors,
        hasErrors,
        clearErrorCreative,
        clearAllErrors,
    }
}

type UseCheckBoxProps = {
    flagValue: boolean
    setErrors: () => void
} & WorkspaceWithLinkDescriptor
const useCheckBox = ({ flagValue, setErrors, ...props }: UseCheckBoxProps) => {
    const { mutate } = useUpdateMarkingFormData(props)

    const checkboxDisabledTooltipOnClick = setErrors

    const checkboxOnChange = useCallback(() => {
        setErrors()
        mutate({
            creative_flag: !flagValue,
        })
    }, [mutate, flagValue, setErrors])

    return {
        checkboxOnChange,
        checkboxDisabledTooltipOnClick,
    }
}

export type CreativeProps = {
    tableRowData: AnalyticsTableRowType
    groupId: number
    draftContractId?: number
    customContractId?: number
    flagValue: boolean
    isLinkDeleted: boolean
    latestKktu: string | null
    isContractConfirmed: boolean
} & UseSavedErid &
    WorkspaceWithLinkDescriptor

export const Creative = ({
    tableRowData,
    groupId,
    draftContractId,
    customContractId,
    flagValue,
    isLinkDeleted,
    isContractConfirmed,
    ...props
}: CreativeProps) => {
    const [wwld] = workspaceWithLinkMethods.extractWwld(props)
    const { activeCreative } = tableRowData
    const creativeId = activeCreative?.id

    const contractId = draftContractId || customContractId

    const {
        erid,
        kktuHook,
        errors,
        eridError,
        setErrors,
        hasErrors,
        clearErrorCreative,
        clearAllErrors,
    } = useValuesWithErrors({
        ...props,
        creativeId,
        contractId,
        isContractConfirmed,
    })

    const createVkOrdCreative = useCreateVkOrdCreative(props)
    const updateVkOrdCreative = useUpdateVkOrdCreative(props)

    const checkboxHook = useCheckBox({
        ...props,
        setErrors,
        flagValue,
    })

    const withClickTooltip = (node: ReactNode) =>
        flagValue ? (
            <ClickTooltip
                enabled
                message={
                    <p>
                        Если хотите отредактировать,
                        <br /> то уберите галочку справа
                    </p>
                }
                style={STYLE.cursorUnset}
                contentStyle={STYLE.clickTooltipContent}
                asChild
            >
                {node}
            </ClickTooltip>
        ) : (
            node
        )

    const allDataFilled = Boolean(
        contractId && activeCreative && kktuHook.state.value && erid
    )

    const disabledCauseDeletedLink = flagValue && isLinkDeleted

    return (
        <MarkingPartLayout
            {...checkboxHook}
            {...(allDataFilled
                ? {}
                : {
                      checkboxDisabled:
                          'Пожалуйста, заполните все параметры, чтобы отметить этот пункт',
                  })}
            {...(disabledCauseDeletedLink
                ? {
                      checkboxDisabled: 'Нельзя отжать так как ссылка удалена',
                  }
                : {})}
            title="Креатив"
            tooltipContent={'Для создания токена требуется договор'}
            Icon={EridIcon}
            checkboxLabel={'Токен получен'}
            flagValue={flagValue}
            labeledCheckboxStyle={STYLE.labeledCheckbox}
            checkboxVariant={eridError ? 'error' : 'green'}
        >
            <div className={classes.content} style={STYLE.content}>
                <div className={cl(classes.item, classes.creative)}>
                    <div className={classes.label}>Название креатива</div>
                    {withClickTooltip(
                        <CreativeCell
                            {...wwld}
                            className={'min-w-0'}
                            groupId={groupId}
                            activeCreative={activeCreative}
                            published={tableRowData.status === 'published'}
                            previewOverlayStyle={STYLE.previewOverlay}
                            previewContentStyle={STYLE.previewContent}
                            selectTriggerStyle={
                                errors.creative
                                    ? STYLE.creativeSelectTriggerError
                                    : STYLE.creativeSelectTriggerNormal
                            }
                            onTouched={clearErrorCreative}
                            disabled={
                                createVkOrdCreative.isPending || flagValue
                            }
                        />
                    )}
                    {errors.creative && (
                        <div className={classes.errorText}>
                            {errors.creative}
                        </div>
                    )}
                </div>

                <div className={cl(classes.item)} style={STYLE.kktu}>
                    <div className={classes.label}>Код ККТУ</div>
                    {withClickTooltip(
                        <KktuSelect
                            {...kktuHook}
                            {...{
                                draftContractId,
                                customContractId,
                                tableRowData,
                                savedErid: props.savedErid,
                            }}
                            disabled={
                                createVkOrdCreative.isPending || flagValue
                                    ? true
                                    : activeCreative
                                      ? undefined
                                      : 'Сначала выберите креатив'
                            }
                            error={errors.kktu}
                            popoverWidth={
                                LAYOUT.width.kktu +
                                LAYOUT.width.token +
                                LAYOUT.gap
                            }
                            mutate={updateVkOrdCreative.mutate}
                        />
                    )}
                    {errors.kktu && (
                        <div className={classes.errorText}>{errors.kktu}</div>
                    )}
                </div>

                <div className={cl(classes.item)} style={STYLE.token}>
                    <div className={classes.label}>
                        Создать и отправить токен в ОРД
                    </div>
                    {erid ? (
                        withClickTooltip(
                            <Erid
                            // faint={flagValue}
                            >
                                {erid}
                            </Erid>
                        )
                    ) : (
                        <>
                            <CreateEridButton
                                {...{
                                    hasErrors,
                                    setErrors,
                                    tableRowData,
                                    draftContractId,
                                    customContractId,
                                    clearAllErrors,
                                    isContractConfirmed,
                                }}
                                kktu={kktuHook.state.value}
                                loading={createVkOrdCreative.isPending}
                                mutate={createVkOrdCreative.mutate}
                            />
                            {eridError && (
                                <div className={classes.errorText}>
                                    {eridError === true
                                        ? 'Создайте токен'
                                        : eridError}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </MarkingPartLayout>
    )
}

type EridProps = {
    children: ReactNode
    faint?: boolean
} & ComponentPropsWithRef<'div'>
const Erid = forwardRef<HTMLDivElement, EridProps>(function Erid(
    { children, faint = false, className, ...props },
    ref
) {
    return (
        <div
            {...props}
            ref={ref}
            className={cl(className, classes.erid, { [classes.faint]: faint })}
        >
            {children}
        </div>
    )
})

type CreateEridButtonProps = {
    mutate: UseCreateVkOrdCreative['mutate']
    loading: boolean
    hasErrors: boolean
    setErrors: () => void
    clearAllErrors: () => void

    kktu: string | null
} & Pick<
    CreativeProps,
    | 'draftContractId'
    | 'customContractId'
    | 'tableRowData'
    | 'isContractConfirmed'
>
const CreateEridButton = ({
    mutate,
    loading,

    hasErrors,
    setErrors,
    clearAllErrors,

    kktu,

    draftContractId,
    customContractId,
    tableRowData,
    isContractConfirmed,
}: CreateEridButtonProps) => {
    let status: 'normal' | 'error' | 'highlighted' | 'loading' = 'normal'
    let onClick: (() => void) | undefined = undefined

    const { activeCreative } = tableRowData

    if (loading) {
        status = 'loading'
    } else if (
        (draftContractId || customContractId) &&
        activeCreative &&
        kktu &&
        isContractConfirmed
    ) {
        status = 'highlighted'
        onClick = () => {
            mutate({
                kktu,
                creativeId: activeCreative.id,
                draftContractId,
                customContractId,
            })
            clearAllErrors()
        }
    } else {
        if (hasErrors) {
            status = 'error'
        }
        onClick = setErrors
    }

    let text: ReactNode
    let extra: ReactNode
    let disabled: boolean

    if (status === 'loading') {
        text = 'Создание'
        extra = <div className={classes.spinner} />
        disabled = true
    } else {
        text = 'Создать'
        extra = <PlusIcon className={classes.icon} />
        disabled = false
    }

    return (
        <button
            {...{ disabled, onClick }}
            className={cl(classes.createButton, classes[status])}
        >
            <div>{text}</div>
            {extra}
        </button>
    )
}
